import { BaseButton } from '@photosi/rn-components/buttons/base-button/BaseButton';
import { IconButtonProps } from './IconButton.types';
import { forwardRef } from 'react';
import { TBaseButtonChildrenProps } from '@photosi/rn-components/buttons/base-button/BaseButton.types';
import { ButtonIconChild } from '@photosi/rn-components/buttons/base-button-children/button-icon-child/ButtonIconChild';
import { BUTTON_SIZES } from '../../buttons/button/Button.constants';
import { styles } from './IconButton.styles';
import { mergeStyles } from 'src/_shared/utils/mergeStyles';
import { ICON_NAMES } from '@photosi/rn-components/icon/icon.helper';
import { ActivityIndicator, View } from 'react-native';
import { withPersonalizedIconButtonStyle } from './withPersonalizedIconButtonStyle';

const BUTTON_SIZE_STYLES_MAP = {
  [BUTTON_SIZES.SMALL]: {
    button: styles.buttonSmall,
    icon: styles.iconSmall,
    iconHover: styles.iconHover,
    iconDisabled: styles.iconDisabled,
    iconPressed: styles.iconPressed,
  },
  [BUTTON_SIZES.REGULAR]: {
    button: styles.buttonDefault,
    icon: styles.iconDefault,
    iconHover: styles.iconHover,
    iconDisabled: styles.iconDisabled,
    iconPressed: styles.iconPressed,
  },
};

const IconButtonComponent = forwardRef(({ size = BUTTON_SIZES.REGULAR, ...buttonProps }: IconButtonProps, ref) => {
  const {
    iconName,
    iconStyle,
    iconDisabledStyle,
    iconHoverStyle,
    iconPressedStyle,
    iconTestID,
    style,
    disabledStyle,
    focusStyle,
    hoverStyle,
    pressedStyle,
    badgeRenderer,
    loading,
    accessibilityLabel: accessibilityLabelProp,
  } = buttonProps;
  const sizeStyles = BUTTON_SIZE_STYLES_MAP[size];

  const buttonStyle = mergeStyles(sizeStyles?.button, style);

  const mergedIconStyle = mergeStyles(sizeStyles?.icon, iconStyle);
  const mergedIconHoverStyle = mergeStyles(sizeStyles?.iconHover, iconHoverStyle);
  const mergedIconDisabledStyle = mergeStyles(sizeStyles?.iconDisabled, iconDisabledStyle);
  const mergedIconPressedStyle = mergeStyles(sizeStyles?.iconPressed, iconPressedStyle);
  const iconColor = Array.isArray(iconStyle)
    ? (iconStyle[iconStyle.length - 1] as any)?.color
    : (iconStyle as any)?.color;

  const accessibilityLabel = accessibilityLabelProp ?? `${iconName} Icon Button`;

  return (
    <BaseButton
      ref={ref}
      {...buttonProps}
      style={buttonStyle}
      disabledStyle={disabledStyle}
      focusStyle={focusStyle}
      hoverStyle={hoverStyle}
      pressedStyle={pressedStyle}
      accessibilityLabel={accessibilityLabel}
    >
      {(baseButtonChildrenProps: TBaseButtonChildrenProps) => {
        return (
          <View style={styles.wrapper}>
            {!loading && (
              <ButtonIconChild
                {...{
                  iconName: iconName as ICON_NAMES,
                  iconStyle: mergedIconStyle,
                  iconDisabledStyle: mergedIconDisabledStyle,
                  iconHoverStyle: mergedIconHoverStyle,
                  iconPressedStyle: mergedIconPressedStyle,
                  iconTestID,
                  badgeRenderer,
                  ...baseButtonChildrenProps,
                }}
              />
            )}
            {loading && (
              <View style={styles.buttonAbsolute}>
                <ActivityIndicator size={16} color={iconColor as string} />
              </View>
            )}
          </View>
        );
      }}
    </BaseButton>
  );
});

IconButtonComponent.displayName = 'IconButtonComponent';

export const IconButton = withPersonalizedIconButtonStyle(IconButtonComponent, styles);
